import React, { useState, useEffect, useRef } from 'react';
import TopMenu from '../../contexts/TopMenu';
import GstinStep from './GstinStep';
import BasicInfoStep from './BasicInfoStep';
import CompanyInfoStep from './CompanyInfoStep';
import BankStep from './BankStep';
import DeclarationStep from './DeclarationStep';
import { ONBOARDING_STEPS, INITIAL_FORM_DATA } from './constants';
import { API_BASE_URL } from '../../config';
import '../../styles/Onboarding.css';

function Onboarding({ email, onLogout }) {
  const [currentStep, setCurrentStep] = useState('gstin');
  const [gstVerified, setGstVerified] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [gstinError, setGstinError] = useState('');
  const [isLoadingGstin, setIsLoadingGstin] = useState(false);
  const [declarationDetails, setDeclarationDetails] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isLoadingDeclaration, setIsLoadingDeclaration] = useState(true);
  const onboardingContentRef = useRef(null);
  
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // Fetch declaration details on component mount
  useEffect(() => {
    const fetchDeclarationDetails = async () => {
      try {
        const authKey = localStorage.getItem('authKey') || '';
        const userId = localStorage.getItem('userId') || '';
        
        const response = await fetch(`${API_BASE_URL}/brands/onboarding/declaration/fetch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json'
          },
          body: JSON.stringify({
            auth_key: authKey,
            user_id: userId
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'success' && data.declaration_details) {
            // Store declaration details
            setDeclarationDetails(data.declaration_details);
            
            // Set form data as read-only because declaration has been submitted
            setIsReadOnly(true);
            
            // Update form data with declaration values
            // We're using setFormData directly which bypasses the readonly check
            setFormData(prev => ({
              ...prev,
              detailsDeclaration: data.declaration_details.detailsDeclaration,
              policiesDeclaration: data.declaration_details.policiesDeclaration
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching declaration details:', error);
      } finally {
        setIsLoadingDeclaration(false);
      }
    };

    fetchDeclarationDetails();
  }, []);

  // Add useEffect to fetch all data when in readonly mode
  useEffect(() => {
    // If we're in readonly mode, fetch GSTIN data regardless of the step
    if (isReadOnly) {
      const fetchGstinDataForReadOnly = async () => {
        try {
          const authKey = localStorage.getItem('authKey') || '';
          const userId = localStorage.getItem('userId') || '';
          
          const response = await fetch(`${API_BASE_URL}/gstin/details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            },
            body: JSON.stringify({
              auth_key: authKey,
              user_id: userId
            })
          });

          if (!response.ok) {
            throw new Error('Failed to fetch GSTIN details');
          }

          const data = await response.json();
          
          if (data.status === 'success' && data.gstin_details) {
            const details = data.gstin_details;
            
            // Update form data with GSTIN details for readonly mode
            setFormData(prev => ({
              ...prev,
              gstin: details.gstin,
              companyName: details.legal_name_of_business || prev.companyName,
              panNumber: details.gstin.slice(2, 12) || prev.panNumber,
              address: details.primary_address !== 'NA' ? details.primary_address : prev.address || 'A-205, 2ND FLOOR, GOTRI ROAD, Vadodara',
              city: details.state_jurisdiction?.includes('Vadodara') ? 'Vadodara' : details.state_jurisdiction?.split('(')[1]?.replace(')', '') || prev.city || 'Vadodara',
              state: details.gstin?.slice(0, 2) === '24' ? 'GUJARAT' : prev.state || 'NA',
              country: 'India',
              pincode: prev.pincode || '390021',
              gstinNumber: details.gstin || prev.gstinNumber,
              entityType: details.constitution_of_business?.toUpperCase().replace(/ /g, '_') || prev.entityType,
              registrationDate: details.date_of_registration || prev.registrationDate,
              taxpayerType: details.taxpayer_type || prev.taxpayerType,
              gstinStatus: details.gstin_status || prev.gstinStatus
            }));
            
            setGstVerified(true);
          }
        } catch (error) {
          console.error('Error fetching GSTIN details for readonly mode:', error);
        }
      };

      fetchGstinDataForReadOnly();
    }
  }, [isReadOnly]);

  useEffect(() => {
    // Add animation for field focus
    const handleFocus = (e) => {
      const formGroup = e.target.closest('.form-group');
      if (formGroup) {
        formGroup.classList.add('focused');
        setFocusedField(e.target.name);
      }
    };

    const handleBlur = (e) => {
      const formGroup = e.target.closest('.form-group');
      if (formGroup) {
        formGroup.classList.remove('focused');
        setFocusedField(null);
      }
    };

    const inputs = document.querySelectorAll('input, select');
    inputs.forEach(input => {
      input.addEventListener('focus', handleFocus);
      input.addEventListener('blur', handleBlur);
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
      });
    };
  }, [currentStep]);

  // Add useEffect for fetching GSTIN details
  useEffect(() => {
    const fetchGstinDetails = async () => {
      if (currentStep === 'gstin' && !gstVerified) {
        setIsLoadingGstin(true);
        try {
          const authKey = localStorage.getItem('authKey') || '';
          const userId = localStorage.getItem('userId') || '';
          
          const response = await fetch(`${API_BASE_URL}/gstin/details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            },
            body: JSON.stringify({
              auth_key: authKey,
              user_id: userId
            })
          });

          if (!response.ok) {
            throw new Error('Failed to fetch GSTIN details');
          }

          const data = await response.json();
          
          if (data.status === 'success' && data.gstin_details) {
            const details = data.gstin_details;
            
            // Update form data with GSTIN details even if readonly
            // We're using setFormData directly which bypasses the readonly check
            setFormData(prev => ({
              ...prev,
              gstin: details.gstin,
              companyName: details.legal_name_of_business,
              panNumber: details.gstin.slice(2, 12),
              address: details.primary_address !== 'NA' ? details.primary_address : 'A-205, 2ND FLOOR, GOTRI ROAD, Vadodara',
              city: details.state_jurisdiction.includes('Vadodara') ? 'Vadodara' : details.state_jurisdiction.split('(')[1]?.replace(')', '') || 'Vadodara',
              state: details.gstin.slice(0, 2) === '24' ? 'GUJARAT' : 'NA',
              country: 'India',
              pincode: '390021',
              gstinNumber: details.gstin,
              entityType: details.constitution_of_business.toUpperCase().replace(/ /g, '_'),
              registrationDate: details.date_of_registration,
              taxpayerType: details.taxpayer_type,
              gstinStatus: details.gstin_status
            }));
            
            setGstVerified(true);
          }
        } catch (error) {
          console.error('Error fetching GSTIN details:', error);
          // Don't show error to user as this is a background check
        } finally {
          setIsLoadingGstin(false);
        }
      }
    };

    fetchGstinDetails();
  }, [currentStep, gstVerified]);

  const handleInputChange = (e, options = {}) => {
    // Skip readonly check if the data is coming from API fetch
    const isFromApi = options.isFromApi || false;
    
    // If form is read-only and not from API, don't allow changes
    if (isReadOnly && !isFromApi) return;
    
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVerifyGSTIN = async () => {
    // If form is read-only, don't allow verification
    if (isReadOnly) return;
    
    setGstinError('');
    setIsAnimating(true);
    setGstVerified(false);
    
    try {
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      const response = await fetch(`${API_BASE_URL}/gstin/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          gstin: formData.gstin
        })
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Invalid GSTIN number. Please check and try again.');
        }
        throw new Error('Failed to verify GSTIN. Please try again later.');
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        const details = data.gstin_details;
        
        // Update form data with GSTIN details even if readonly
        // We're using setFormData directly which bypasses the readonly check
        setFormData(prev => ({
          ...prev,
          gstin: details.gstin,
          companyName: details.legal_name_of_business,
          panNumber: details.gstin.slice(2, 12),
          address: details.primary_address !== 'NA' ? details.primary_address : 'A-205, 2ND FLOOR, GOTRI ROAD, Vadodara',
          city: details.state_jurisdiction.includes('Vadodara') ? 'Vadodara' : details.state_jurisdiction.split('(')[1]?.replace(')', '') || 'Vadodara',
          state: details.gstin.slice(0, 2) === '24' ? 'GUJARAT' : 'NA',
          country: 'India',
          pincode: '390021',
          gstinNumber: details.gstin,
          entityType: details.constitution_of_business.toUpperCase().replace(/ /g, '_'),
          registrationDate: details.date_of_registration,
          taxpayerType: details.taxpayer_type,
          gstinStatus: details.gstin_status
        }));
        
        setGstVerified(true);
      } else {
        throw new Error(data.message || 'GSTIN verification failed. Please try again.');
      }
    } catch (error) {
      console.error('GSTIN verification error:', error);
      setGstinError(error.message);
    } finally {
      setIsAnimating(false);
    }
  };

  const handleStepClick = (stepId) => {
    if (currentStep === stepId || isAnimating) return;
    
    const currentIndex = ONBOARDING_STEPS.findIndex(step => step.id === currentStep);
    const targetIndex = ONBOARDING_STEPS.findIndex(step => step.id === stepId);
    
    if (targetIndex <= currentIndex + 1 && (gstVerified || currentStep !== 'gstin')) {
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentStep(stepId);
        setIsAnimating(false);
        
        if (onboardingContentRef.current) {
          onboardingContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  };

  const isStepCompleted = (stepId) => {
    const currentStepIndex = ONBOARDING_STEPS.findIndex(step => step.id === currentStep);
    const stepIndex = ONBOARDING_STEPS.findIndex(step => step.id === stepId);
    return stepIndex < currentStepIndex;
  };

  const handleNextStep = () => {
    const currentIndex = ONBOARDING_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex < ONBOARDING_STEPS.length - 1) {
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentStep(ONBOARDING_STEPS[currentIndex + 1].id);
        setIsAnimating(false);
        
        if (onboardingContentRef.current) {
          onboardingContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  };

  const handlePrevStep = () => {
    const currentIndex = ONBOARDING_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex > 0) {
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentStep(ONBOARDING_STEPS[currentIndex - 1].id);
        setIsAnimating(false);
        
        if (onboardingContentRef.current) {
          onboardingContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  };

  const renderStepContent = () => {
    const commonProps = {
      formData,
      handleInputChange,
      focusedField,
      isAnimating,
      handlePrevStep,
      handleNextStep,
      isReadOnly
    };

    switch (currentStep) {
      case 'gstin':
        return (
          <GstinStep
            {...commonProps}
            handleVerifyGSTIN={handleVerifyGSTIN}
            gstinError={gstinError}
            gstVerified={gstVerified}
          />
        );
      case 'basic':
        return <BasicInfoStep {...commonProps} />;
      case 'company':
        return <CompanyInfoStep {...commonProps} />;
      case 'bank':
        return <BankStep {...commonProps} />;
      case 'declaration':
        return <DeclarationStep 
          formData={formData} 
          handleInputChange={handleInputChange} 
          isReadOnly={isReadOnly}
        />;
      default:
        return (
          <div className="onboarding-section">
            <h2>{currentStep}</h2>
            <p>This section is under development.</p>
          </div>
        );
    }
  };

  // Format timestamp into human-readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Merchant Onboarding" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="onboarding-content" ref={onboardingContentRef}>
          <div className="onboarding-progress">
            {ONBOARDING_STEPS.map((step) => (
              <div
                key={step.id}
                className={`progress-step ${
                  isStepCompleted(step.id) ? 'completed' : ''
                } ${currentStep === step.id ? 'active' : ''}`}
                onClick={() => handleStepClick(step.id)}
              >
                <div className="step-icon">
                  {isStepCompleted(step.id) ? '✓' : step.icon}
                </div>
                <div className="step-label">{step.label}</div>
              </div>
            ))}
          </div>
          
          {/* Show submission timestamp if form is readonly */}
          {isReadOnly && declarationDetails && declarationDetails.created_at && (
            <div className="submission-timestamp">
              Form submitted on: {formatDate(declarationDetails.created_at)}
            </div>
          )}
          
          {isLoadingDeclaration ? (
            <div className="loading-indicator">Loading...</div>
          ) : (
            <div className={`step-content-container ${isAnimating || isLoadingGstin ? 'animating' : ''}`}>
              {renderStepContent()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Onboarding; 